/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { action, computed, observable } from 'mobx';
import { OmitFunctions } from 'src/core/stores/types';
import { CustomTextClassStore } from 'src/customText/core/stores/CustomTextClassStore';
import { CustomTextEntityLabelStore } from 'src/customText/core/stores/CustomTextEntityLabelStore';
import { DatasetType } from 'src/modules/modelTraining/types/trainingTypes';

export type CustomTextDocumentStoreData = OmitFunctions<CustomTextDocumentStore>;

export type CustomTextDocumentMetadata = Pick<
    CustomTextDocumentStore,
    'name' | 'text' | 'contentSize' | 'language' | 'entityLabels' | 'isInitialized' | 'documentDataset' | 'classLabels'
>;

export const emptyCustomTextDocumentMetadata: CustomTextDocumentMetadata = {
    name: '',
    text: '',
    contentSize: 0,
    language: '',
    classLabels: [],
    entityLabels: [],
    isInitialized: false,
    documentDataset: null
};

export class CustomTextDocumentStore {
    public name: string;

    public text: string;

    @observable public contentSize: number;

    @observable public language: string;

    @observable public isInitialized = false;

    @observable public documentDataset: DatasetType;

    @observable public classLabels: CustomTextClassStore[];

    @observable public entityLabels: CustomTextEntityLabelStore[];

    constructor(documentData: Partial<CustomTextDocumentStoreData>) {
        this.name = documentData.name;
        this.text = documentData.text ?? '';
        this.contentSize = documentData.contentSize;
        this.language = documentData.language;
        this.classLabels = documentData.classLabels ?? [];
        this.entityLabels = documentData.entityLabels ?? [];
        this.isInitialized = documentData.isInitialized ?? false;
        this.documentDataset = documentData.documentDataset ?? null;

        if (documentData.text && !documentData.isInitialized) {
            this.initialize(documentData.text);
        }
    }

    /**
     * Checks if the documents contains labels or not
     */
    @computed
    public get isLabeledDocument() {
        return this.classLabels?.length > 0 || this.entityLabels?.length > 0;
    }

    /**
     * Updates the document tokenized text.
     */
    @action
    public initialize(text: string) {
        this.text = text;
        this.isInitialized = true;
    }

    /**
     * Sets classes' labels to the document
     * @param classesLabels new labels to be added
     */
    @action
    public setClassLabels(classLabels: CustomTextClassStore[]) {
        this.classLabels = classLabels;
    }

    /**
     * Adds class labels to the document
     * @param classesLabels new labels to be added
     */
    @action
    public addClassLabels(classLabels: CustomTextClassStore[]) {
        this.classLabels.push(...classLabels);
    }

    /**
     * deletes class labels from the document
     * @param classesLabels labels to be deleted
     */
    @action
    public deleteClassLabels(classLabelsIds: string[]) {
        this.classLabels = this.classLabels.filter(label => !classLabelsIds.find(id => id === label.id));
    }

    /**
     * Sets entity labels of the document
     * @param entityLabels new labels to be added
     */
    @action
    public setEntityLabels(entityLabels: CustomTextEntityLabelStore[]) {
        this.entityLabels = entityLabels;
    }

    /**
     * Adds entity labels to the document
     * @param entityLabels new labels to be added
     */
    @action
    public addEntityLabels(entityLabels: CustomTextEntityLabelStore[]) {
        this.entityLabels.push(...entityLabels);
    }

    /**
     * deletes entity labels from the document labels
     * @param entityIds labels to be deleted
     */
    @action
    public deleteEntityLabels(entityIds: string[]) {
        this.entityLabels = this.entityLabels.filter(label => !entityIds.includes(label.entityId));
    }

    /**
     * Sets document culture.
     */
    @action
    public setCulture(culture: string) {
        this.language = culture;
    }

    /**
     * Sets the dataset which the document belongs to
     */
    @action
    public setDocumentDataSet(ds: DatasetType) {
        this.documentDataset = ds;
    }
}
